import { useEffect, useMemo, useRef, useState } from 'react'

export interface RolodexItem {
  id: string
  group: 'SESSIONS' | 'SERVERS' | 'ACTIONS'
  title: string
  meta?: string
  onSelect: () => void
}

export function Rolodex({
  open,
  onClose,
  items,
}: {
  open: boolean
  onClose: () => void
  items: RolodexItem[]
}) {
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setCursor(0)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    if (!query.trim()) return items
    const q = query.toLowerCase()
    return items.filter(
      (i) =>
        i.title.toLowerCase().includes(q) ||
        i.group.toLowerCase().includes(q) ||
        (i.meta || '').toLowerCase().includes(q),
    )
  }, [items, query])

  useEffect(() => {
    if (cursor >= filtered.length) setCursor(0)
  }, [filtered, cursor])

  if (!open) return null

  const grouped = groupItems(filtered)

  function select(index: number) {
    const item = filtered[index]
    if (!item) return
    onClose()
    item.onSelect()
  }

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => Math.min(c + 1, Math.max(0, filtered.length - 1)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => Math.max(0, c - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(cursor)
    }
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Search">
      <div
        className="absolute inset-0 bg-bg/85 animate-fade-in"
        onClick={onClose}
      />
      <div className="relative mx-auto mt-[14vh] max-w-xl panel animate-fade-in">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-line bg-surface">
          <span className="font-mono text-accent">$</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setCursor(0)
            }}
            onKeyDown={onInputKey}
            placeholder="search sessions, servers, actions…"
            className="flex-1 bg-transparent outline-none font-mono text-[15.5px] text-fg placeholder:text-fg-dim"
          />
          <span className="font-mono text-[11.5px] text-fg-dim">[esc]</span>
        </div>
        <div className="max-h-[54vh] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center font-mono text-fg-mute text-[14px]">
              no matches for &quot;{query}&quot;
            </div>
          )}
          {grouped.map(([group, rows]) => (
            <div key={group} className="py-1.5">
              <div className="px-3 pt-2 pb-1 font-mono uppercase tracking-[0.12em] text-[11px] text-fg-dim">
                // {group.toLowerCase()}
              </div>
              <ul>
                {rows.map((row) => {
                  const index = filtered.indexOf(row)
                  const active = index === cursor
                  return (
                    <li key={row.id}>
                      <button
                        onClick={() => select(index)}
                        onMouseEnter={() => setCursor(index)}
                        className={`w-full text-left px-3 py-1.5 flex items-center gap-2 font-mono text-[14.5px] transition-colors ${
                          active
                            ? 'bg-accent text-bg'
                            : 'text-fg-soft hover:bg-surface'
                        }`}
                      >
                        <span className="w-3 shrink-0">
                          {active ? '▶' : ' '}
                        </span>
                        <span className="flex-1 truncate">{row.title}</span>
                        {row.meta && (
                          <span
                            className={`text-[12px] shrink-0 ${
                              active ? 'text-bg/70' : 'text-fg-dim'
                            }`}
                          >
                            {row.meta}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-3 py-1.5 border-t border-line bg-surface flex items-center justify-between font-mono text-[11.5px] text-fg-dim">
          <span>↑↓ navigate · ⏎ select · esc close</span>
          <span>{filtered.length} results</span>
        </div>
      </div>
    </div>
  )
}

function groupItems(items: RolodexItem[]): [RolodexItem['group'], RolodexItem[]][] {
  const order: RolodexItem['group'][] = ['ACTIONS', 'SERVERS', 'SESSIONS']
  const map = new Map<RolodexItem['group'], RolodexItem[]>()
  for (const i of items) {
    if (!map.has(i.group)) map.set(i.group, [])
    map.get(i.group)!.push(i)
  }
  return order.filter((g) => map.has(g)).map((g) => [g, map.get(g)!])
}
